import { ColumnDef, PaginationState, Row } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useGetIntegrationCallsMutation } from '../../../services';
import {
  ComponentSize,
  DateFormat,
  ExternalCallType,
  IntegrationCall,
  IntegrationService,
  TextColor,
  TimeFormat,
} from '../../../types';
import { conditionalArray, formatSecondsToDuration, isValidURL } from '../../../utils';
import {
  ButtonGroup,
  Checkbox,
  DataTable,
  DatePickerRange,
  TextButton,
  Typography,
  TypographyType,
} from '../../shared';
import CallInfoCell from './CallInfoCell';
import IntegrationCallsFilters from './IntegrationCallsFilters';

const INTEGRATION_CALLS_ITEMS_PER_PAGE = 100;

interface IntegrationCallsTableProps {
  service: IntegrationService;
  isLoading: boolean;
  handleImportCalls: (service: IntegrationService, selectedCallIds: string[], callType?: ExternalCallType) => void;
  onBack: () => void;
  selectMultipleCalls?: boolean;
}

const IntegrationCallsTable = ({
  service,
  isLoading,
  handleImportCalls,
  onBack,
  selectMultipleCalls = false,
}: IntegrationCallsTableProps) => {
  // State
  const [selectedParticipants, setSelectedParticipants] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<DatePickerRange>();

  const [searchCallId, setSearchCallId] = useState<string | undefined>(undefined);
  const [searchError, setSearchError] = useState<string>('');

  const [selectedCallIds, setSelectedCallIds] = useState<string[]>([]);

  // Call type filteris only available for Salesloft
  const [selectedCallType, setSelectedCallType] = useState(
    service === IntegrationService.SALESLOFT ? ExternalCallType.PHONE_CALL : undefined
  );

  const [cursors, setCursors] = useState<string[]>([]);
  const [currentCursorIndex, setCurrentCursorIndex] = useState(-1);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: INTEGRATION_CALLS_ITEMS_PER_PAGE,
  });

  // Mutations
  const [getIntegrationCalls, { data: integrationCalls, isLoading: isLoadingIntegrationCalls }] =
    useGetIntegrationCallsMutation();

  const { calls = [], pagination: integrationCallsPagination } = integrationCalls || {};
  const { cursor: nextCursor, totalPages = 0 } = integrationCallsPagination || {};

  // Search call ID can be a call ID or a sharable call link
  const extractCallId = useCallback(() => {
    if (!searchCallId) return;

    // If the search call ID is a valid URL, extract the call ID from the URL
    if (isValidURL(searchCallId)) {
      const url = new URL(searchCallId);
      const callId = url.searchParams.get('id');
      // If the call ID is not found, set the search error
      if (!callId) {
        setSearchError('Invalid call link');
        return undefined;
      }
      return callId;
    }
    // If the search call ID is not a valid URL, return the search call ID directly
    return searchCallId;
  }, [searchCallId]);

  // Filters to pass to the getGongCalls mutation
  const filters = useMemo(
    () => ({
      userIds: selectedParticipants.length ? selectedParticipants : undefined,
      startTime: selectedDate,
      callId: extractCallId(),
      callType: selectedCallType,
    }),
    [selectedParticipants, selectedDate, extractCallId, selectedCallType]
  );

  // Indices of the active rows according to the selected calls IDs
  const activeRowIndices = useMemo(
    () => calls.map((call, index) => (selectedCallIds.includes(call.id) ? index : -1)).filter((index) => index !== -1),
    [calls, selectedCallIds]
  );

  // Get the Gong calls
  useEffect(() => {
    getIntegrationCalls({ service, ...filters });
  }, [getIntegrationCalls, filters, service]);

  // Toggle the selection of a call
  const toggleRowSelection = useCallback(
    (callId: string, checked: boolean) => {
      setSelectedCallIds((prevSelected) => {
        if (!selectMultipleCalls) {
          // In single selection mode, either select only this call or deselect it
          return checked ? [callId] : [];
        }
        // In multiple selection mode, add or remove from selection array
        const newSelection = checked ? [...prevSelected, callId] : prevSelected.filter((id) => id !== callId);
        return newSelection;
      });
    },
    [selectMultipleCalls]
  );

  const resetPagination = () => {
    // Reset pagination state and fetch first page
    setCursors([]);
    setCurrentCursorIndex(-1);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // Reset to page 1 when filters change.
  useEffect(() => {
    if (pagination.pageIndex !== 0) {
      resetPagination();
    }
    // We want this to execute only on filters change
  }, [filters]);

  // Handle pagination controls
  const handleNextPage = useCallback(() => {
    if (!nextCursor) return;

    // Add new cursor to our cursors array
    // To be able to fetch previous pages
    setCursors((prev) => [...prev, nextCursor]);
    // Update current cursor index
    setCurrentCursorIndex((prev) => prev + 1);
    // Update pagination state
    setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
    // Fetch next page with cursor
    getIntegrationCalls({
      service: IntegrationService.GONG,
      ...filters,
      cursor: nextCursor,
    });
  }, [filters, nextCursor]);

  const handlePrevPage = useCallback(() => {
    if (currentCursorIndex < 0) {
      resetPagination();
      return;
    }

    // Update pagination state
    setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex - 1 }));
    // Update current cursor index
    setCurrentCursorIndex((prev) => prev - 1);
    // Fetch previous page with cursor
    getIntegrationCalls({
      service: IntegrationService.GONG,
      ...filters,
      cursor: cursors[currentCursorIndex - 1],
    });
  }, [currentCursorIndex, cursors, filters]);

  const parsedData = useMemo(
    () =>
      calls.map((call) => ({
        ...call,
        onClick: () => {
          const isSelected = selectedCallIds.includes(call.id);
          toggleRowSelection(call.id, !isSelected);
        },
      })),
    [calls, selectedCallIds, toggleRowSelection]
  );

  const SelectionCheckbox = ({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) => (
    <div className="flex justify-center">
      <Checkbox checked={checked} setChecked={onChange} size={ComponentSize.X_SMALL} />
    </div>
  );

  // Define columns for the data table.
  const columns: ColumnDef<IntegrationCall>[] = useMemo(
    () => [
      ...conditionalArray(selectMultipleCalls, {
        accessorKey: 'isSelected',
        size: 1 / 10,
        header: () => (
          <SelectionCheckbox
            checked={calls.length > 0 && calls.every((call) => selectedCallIds.includes(call.id))}
            onChange={(checked) => {
              setSelectedCallIds(checked ? calls.map((call) => call.id) : []);
            }}
          />
        ),
        cell: ({ row }: { row: Row<IntegrationCall> }) => {
          const isSelected = selectedCallIds.includes(row.original.id);
          return (
            <SelectionCheckbox
              checked={isSelected}
              onChange={(checked) => {
                toggleRowSelection(row.original.id, checked);
              }}
            />
          );
        },
      }),
      {
        accessorKey: 'callInfo',
        size: 4 / 10,
        header: 'Call info',
        cell: ({ row }) => <CallInfoCell call={row.original} />,
      },
      {
        accessorKey: 'duration',
        size: 2.5 / 10,
        header: 'Duration',
        cell: ({ row }) => formatSecondsToDuration(row.original.duration),
      },
      {
        accessorKey: 'callDate',
        header: 'Call date',
        size: 2.5 / 10,
        cell: ({ row }) => {
          const date = row.original?.callDate;
          return (
            <div className="flex flex-col gap-1">
              <Typography color={TextColor.TERTIARY} type={TypographyType.X_SMALL}>
                {dayjs(date).format(TimeFormat.SHORT_TIME)}
              </Typography>
              <Typography color={TextColor.SECONDARY} type={TypographyType.X_SMALL}>
                {dayjs(date).format(DateFormat.MONTH_DAY)}
              </Typography>
            </div>
          );
        },
      },
    ],
    [calls, selectedCallIds, toggleRowSelection, selectMultipleCalls]
  );

  const actionButtonText = selectMultipleCalls
    ? `Import ${selectedCallIds.length > 0 ? selectedCallIds.length : ''} call${selectedCallIds.length === 1 ? '' : 's'}`
    : 'Next';

  return (
    <div className="flex h-full flex-col items-end justify-between gap-4 overflow-auto">
      <div className="flex h-[calc(100%-64px)] w-full gap-4">
        <div className="flex-1">
          <IntegrationCallsFilters
            service={service}
            selectedParticipants={selectedParticipants}
            setSelectedParticipants={setSelectedParticipants}
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            searchCallId={searchCallId}
            setSearchCallId={setSearchCallId}
            searchError={searchError}
            setSearchError={setSearchError}
            selectedCallType={selectedCallType}
            setSelectedCallType={setSelectedCallType}
            setSelectedCallIds={setSelectedCallIds}
          />
        </div>
        <DataTable
          columns={columns}
          data={parsedData}
          isLoading={isLoadingIntegrationCalls}
          activeRowIndices={activeRowIndices}
          paginationControls={{
            pagination,
            setPagination,
            totalPages,
            onNextClick: handleNextPage,
            onPrevClick: handlePrevPage,
          }}
        />
      </div>
      <ButtonGroup className="pb-[1px]">
        <TextButton
          text={actionButtonText}
          onClick={() => handleImportCalls(service, selectedCallIds, selectedCallType)}
          loading={isLoading}
          disabled={!selectedCallIds.length}
        />
        <TextButton text="Back" onClick={onBack} />
      </ButtonGroup>
    </div>
  );
};

export default IntegrationCallsTable;
